import { type SdkKey, getSdkInfoByKey } from '@gonfalon/sdk-info/sdkInfo';

export type FilterCategory =
  | 'all'
  | 'backendAndServer'
  | 'desktop'
  | 'edgeAndCloud'
  | 'mobile'
  | 'webAndFrontend'
  | 'observabilitySupported';

export const filterDisplayNames: Record<FilterCategory, string> = {
  all: 'All SDKs',
  backendAndServer: 'Backend and server',
  desktop: 'Desktop',
  edgeAndCloud: 'Edge and cloud',
  mobile: 'Mobile',
  webAndFrontend: 'Web and frontend',
  observabilitySupported: 'Observability supported',
};

/**
 * Determines which category an SDK belongs to based on its metadata.
 */
export function getSdkCategory(sdk: SdkKey): FilterCategory | null {
  const metadata = getSdkInfoByKey(sdk);
  if (!metadata) {
    return null;
  }

  // Desktop: Electron
  if (sdk === 'electron-client-sdk') {
    return 'desktop';
  }

  if (metadata.docsPath.includes('/edge/')) {
    return 'edgeAndCloud';
  }

  // Mobile: initKey includes mobile
  if (metadata.initKey.includes('mobile')) {
    return 'mobile';
  }

  // Backend and server: initKey includes serverSide
  if (metadata.initKey.includes('serverSide')) {
    return 'backendAndServer';
  }

  // Web and frontend: initKey includes clientSide but not desktop or edge
  if (metadata.initKey.includes('clientSide')) {
    return 'webAndFrontend';
  }

  return null;
}

/**
 * Checks if an SDK matches the given filter category.
 */
export function matchesFilter(sdk: SdkKey, filterValue: FilterCategory): boolean {
  if (filterValue === 'all') {
    return true;
  }

  const metadata = getSdkInfoByKey(sdk);
  if (!metadata) {
    return false;
  }

  // Observability supported filter
  if (filterValue === 'observabilitySupported') {
    return metadata.observabilitySupported === true;
  }

  // Other category filters
  const category = getSdkCategory(sdk);
  return category === filterValue;
}

/**
 * Filters SDKs by category and search query.
 */
export function filterSdks(
  sdks: readonly SdkKey[],
  filterValue: FilterCategory,
  searchValue: string,
  options?: { sort?: boolean },
): SdkKey[] {
  let filtered = [...sdks];

  // Filter by category
  if (filterValue !== 'all') {
    filtered = filtered.filter((sdk) => matchesFilter(sdk, filterValue));
  }

  // Filter by search if there's a search value
  if (searchValue) {
    const lowerSearch = searchValue.trim().toLowerCase();
    filtered = filtered.filter((sdk) => {
      const metadata = getSdkInfoByKey(sdk);
      return metadata?.name?.toLowerCase().includes(lowerSearch) ?? false;
    });
  }

  // Sort alphabetically by name if requested
  if (options?.sort) {
    filtered = [...filtered].sort((a, b) => {
      const aName = getSdkInfoByKey(a)?.name ?? a;
      const bName = getSdkInfoByKey(b)?.name ?? b;
      return aName.localeCompare(bName);
    });
  }

  return filtered;
}
